import {
    createContext,
    useState,
    useCallback,
    useContext,
    ReactNode,
    ComponentProps,
    JSX,
} from "react";
import {
    Modal,
    ModalClose,
    ModalContent,
    ModalDescription,
    ModalFooter,
    ModalHeader,
    ModalTitle,
} from "~/components/molecules/Modal";
import { Button } from "~/components/atoms/Button";

type DialogTypes = "alert" | "confirmation" | "modal";

type BaseDialogProps = {
    body: ReactNode;
    description?: string;
    onClose?: () => void;
    title: ReactNode;
    type: DialogTypes;
};

type BaseModalProps = {
    footer?: ReactNode;
};

type AlertDialogProps = Omit<BaseDialogProps, "title"> &
    BaseModalProps & {
        title?: ReactNode;
        type: "alert";
    };

type ModalDialogProps = BaseDialogProps &
    BaseModalProps & {
        type: "modal";
    };

type ConfirmationDialogProps = BaseDialogProps & {
    type: "confirmation";
    onConfirm: () => void;
    confirmButtonLabel?: string;
    confirmButtonStyle?: ComponentProps<typeof Button>["buttonStyle"];
    closeButtonLabel?: string;
};

type DialogProps = AlertDialogProps | ConfirmationDialogProps | ModalDialogProps;

interface ModalContextProps {
    openModal: (dialog: DialogProps) => void;
    closeModal: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const AlertModal = ({ body, description, footer, title, onClose }: AlertDialogProps) => (
    <Modal open onOpenChange={onClose}>
        <ModalContent className="w-[80dvw] rounded-3xl sm:w-fit">
            <ModalDescription>{description || "Modal Description"}</ModalDescription>
            <ModalHeader withShadows={!!title} withBorders={!!title}>
                <ModalTitle className={title ? "sr-only" : ""}>{title}</ModalTitle>
            </ModalHeader>
            <div className="p-5">{body}</div>
            {footer && <ModalFooter withBorders={false}>{footer}</ModalFooter>}
        </ModalContent>
    </Modal>
);

export const ConfirmationModal = ({
    body,
    description,
    onConfirm,
    confirmButtonLabel,
    confirmButtonStyle,
    onClose,
    closeButtonLabel,
    title,
}: ConfirmationDialogProps) => (
    <Modal open onOpenChange={onClose}>
        <ModalContent className="w-[80vw] rounded-3xl sm:w-fit">
            <ModalDescription>{description || "Modal Description"}</ModalDescription>
            <ModalHeader withClose={false} withShadows={false}>
                <ModalTitle heading="confirmation">{title}</ModalTitle>
            </ModalHeader>
            <div className="p-5">{body}</div>
            <ModalFooter withShadows={false} withBorders={false} className="flex justify-end gap-4">
                <ModalClose
                    onClick={onClose}
                    className="focusable pointer-events-auto animate-fade cursor-pointer text-blue-80"
                >
                    {closeButtonLabel ?? "Cancel"}
                </ModalClose>
                <Button
                    buttonStyle={confirmButtonStyle ?? "secondary"}
                    size="lg"
                    onClick={onConfirm}
                >
                    {confirmButtonLabel ?? "Confirm"}
                </Button>
            </ModalFooter>
        </ModalContent>
    </Modal>
);

const DefaultModal = ({ body, description, footer, title, onClose }: ModalDialogProps) => (
    <Modal open onOpenChange={onClose}>
        <ModalContent className="size-full sm:size-fit sm:max-w-[80dvw] sm:rounded-3xl">
            <ModalDescription>{description || "Modal Description"}</ModalDescription>
            <ModalHeader withShadows={!!title}>
                <ModalTitle>{title}</ModalTitle>
            </ModalHeader>
            <div className="p-5">{body}</div>
            {footer && <ModalFooter>{footer}</ModalFooter>}
        </ModalContent>
    </Modal>
);

const modalComponents: {
    alert: (props: AlertDialogProps) => JSX.Element;
    confirmation: (props: ConfirmationDialogProps) => JSX.Element;
    modal: (props: ModalDialogProps) => JSX.Element;
} = {
    alert: AlertModal,
    confirmation: ConfirmationModal,
    modal: DefaultModal,
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
    const [dialogProps, setDialogProps] = useState<DialogProps | null>(null);
    const openModal = useCallback((dialog: DialogProps) => setDialogProps(dialog), []);
    const closeModal = useCallback(() => setDialogProps(null), []);
    const renderDialog = () => {
        if (!dialogProps) return null;

        const onClose = () => {
            closeModal();
            dialogProps.onClose?.();
        };
        switch (dialogProps.type) {
            case "alert":
                return modalComponents.alert(dialogProps);
            case "confirmation":
                return modalComponents.confirmation({
                    ...dialogProps,
                    onClose,
                    onConfirm: () => {
                        dialogProps.onConfirm();
                        closeModal();
                    },
                });
            case "modal":
                return modalComponents.modal({
                    ...dialogProps,
                    onClose,
                });
            default:
                return null;
        }
    };

    return (
        <ModalContext.Provider value={{ openModal, closeModal }}>
            {children}
            {renderDialog()}
        </ModalContext.Provider>
    );
};

export const useModal = (): ModalContextProps => {
    const context = useContext(ModalContext);
    if (!context) throw new Error("useModal must be used within a ModalProvider");
    return context;
};
